import { createAsyncThunk } from '@reduxjs/toolkit';

import type { RootState } from '@/store';
import { getErrorMessage } from '@/shared/utils/error';
import { reportError } from '@/shared/utils/reportError';

import type {
  CadastroResultado,
  ConsultaCnpjResultado,
  ConsultaPagamentoResultado,
  PagamentoResultado,
  PlanoApi,
} from '../types';
import { notificacaoSelfService } from './services';

export const carregarPlanosSelf = createAsyncThunk<PlanoApi[], void, { rejectValue: string }>(
  'notificacaoSelf/carregarPlanos',
  async (_, { rejectWithValue }) => {
    try {
      return await notificacaoSelfService.carregarPlanos();
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Erro ao carregar os planos'));
    }
  },
);

export const consultarCnpjSelf = createAsyncThunk<
  ConsultaCnpjResultado,
  void,
  { rejectValue: string; state: RootState }
>('notificacaoSelf/consultarCnpj', async (_, { getState, signal, rejectWithValue }) => {
  try {
    const { form } = getState().notificacaoSelf;
    return await notificacaoSelfService.consultarCnpj(form.cnpj, signal);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Erro ao consultar o CNPJ'));
  }
});

export const enviarCadastroSelf = createAsyncThunk<
  CadastroResultado,
  { irParaRevisao: boolean },
  { rejectValue: string; state: RootState }
>('notificacaoSelf/enviarCadastro', async (_, { getState, signal, rejectWithValue }) => {
  try {
    const { form, planoTier, planos } = getState().notificacaoSelf;
    const plano = planos.find((p) => p.tier === planoTier);
    if (!plano) {
      return rejectWithValue('Planos indisponíveis no momento. Recarregue a página.');
    }
    return await notificacaoSelfService.enviarCadastro(form, plano.variationId, signal);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Erro ao enviar o cadastro'));
  }
});

export const iniciarPagamentoSelf = createAsyncThunk<
  PagamentoResultado,
  void,
  { rejectValue: string; state: RootState }
>('notificacaoSelf/iniciarPagamento', async (_, { getState, signal, rejectWithValue }) => {
  try {
    const { leadId, accessToken } = getState().notificacaoSelf;
    if (!leadId || !accessToken) {
      return rejectWithValue('Sua sessão do cadastro expirou, recomece.');
    }
    return await notificacaoSelfService.iniciarPagamento({ leadId, accessToken }, signal);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Erro ao iniciar o pagamento'));
  }
});

export const criarSessaoSelf = createAsyncThunk<
  void,
  void,
  { rejectValue: string; state: RootState }
>('notificacaoSelf/criarSessao', async (_, { getState, rejectWithValue }) => {
  const { leadId, accessToken } = getState().notificacaoSelf;
  if (!leadId || !accessToken) {
    return rejectWithValue('Sessão do cadastro indisponível');
  }
  try {
    await notificacaoSelfService.criarSessao({ leadId, accessToken });
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Erro ao criar sessão de primeiro acesso'));
  }
});

export const consultarPagamentoSelf = createAsyncThunk<
  ConsultaPagamentoResultado,
  void,
  { rejectValue: string; state: RootState }
>('notificacaoSelf/consultarPagamento', async (_, { getState, signal, rejectWithValue }) => {
  try {
    const { leadId, accessToken } = getState().notificacaoSelf;
    if (!leadId || !accessToken) {
      return rejectWithValue('Sua sessão do cadastro expirou, recomece.');
    }
    return await notificacaoSelfService.consultarPagamento({ leadId, accessToken }, signal);
  } catch (error) {
    if (!signal.aborted) {
      reportError(error instanceof Error ? error : new Error(getErrorMessage(error)), {
        source: 'notificacaoSelf.consultarPagamento',
      });
    }
    return rejectWithValue(getErrorMessage(error, 'Erro ao consultar o pagamento'));
  }
});
